import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Divider, Form, Input, Typography } from "antd";
import { sendEmailVerification, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useTranslation } from "react-i18next";
import { getFleetRoleLabel } from "../auth/fleetRoles";
import { AuthContext } from "../context/AuthContext";
import Footer from "../components/footer/Footer";
import LanguageMenu from "../components/languageMenu/LanguageMenu";
import { auth, functions } from "../firebase";
import { isSecurePassword } from "../utils/authValidation";

function extractErrorMessage(error, fallback) {
  const rawMessage = String(error?.message || "").replace(/^FirebaseError:\s*/i, "").trim();
  return rawMessage || fallback;
}

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function signInAfterInviteSignup(email, password) {
  let lastError = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      lastError = error;
      if (attempt < 2) {
        await delay(250 * (attempt + 1));
      }
    }
  }

  throw lastError;
}

export default function AcceptMemberInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading, authenticateUser } = useContext(AuthContext);

  const [invitePreview, setInvitePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const [showSignInForm, setShowSignInForm] = useState(false);

  const payload = useMemo(
    () => ({
      fleetId: String(searchParams.get("fleetId") || "").trim(),
      inviteId: String(searchParams.get("inviteId") || "").trim(),
      token: String(searchParams.get("token") || "").trim(),
    }),
    [searchParams]
  );

  const isValidLink = Boolean(payload.fleetId && payload.inviteId && payload.token);
  const currentUser = user || auth.currentUser || null;
  const currentUserEmail = String(currentUser?.email || "")
    .trim()
    .toLowerCase();
  const inviteEmail = String(invitePreview?.email || "")
    .trim()
    .toLowerCase();
  const isInviteEmailUser = Boolean(currentUserEmail && inviteEmail && currentUserEmail === inviteEmail);
  const canCreateAccount = Boolean(displayName.trim()) && isSecurePassword(password) && password === passwordConfirm;

  useEffect(() => {
    if (!isValidLink) {
      setPreviewLoading(false);
      setInvitePreview(null);
      return;
    }

    let cancelled = false;

    const loadInvite = async () => {
      setPreviewLoading(true);
      setError("");

      try {
        const fn = httpsCallable(functions, "get_member_invite");
        const response = await fn(payload);
        if (cancelled) return;
        setInvitePreview(response?.data?.data || null);
      } catch (inviteError) {
        if (cancelled) return;
        setInvitePreview(null);
        setError(
          extractErrorMessage(
            inviteError,
            t("invite.loadError", { defaultValue: "Could not load this invite." })
          )
        );
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    loadInvite();

    return () => {
      cancelled = true;
    };
  }, [isValidLink, payload, t]);

  const sendVerificationAndReturnToLogin = async (firebaseUser) => {
    try {
      await sendEmailVerification(firebaseUser, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
      await signOut(auth).catch(() => {});
      navigate("/login?verifyEmail=1", { replace: true });
      return true;
    } catch (verificationError) {
      console.error("Invite verification email error:", verificationError);
      await signOut(auth).catch(() => {});
      navigate("/login?verifyEmailRetry=1", { replace: true });
      return false;
    }
  };

  const handleAcceptForSignedInUser = async (firebaseUser = auth.currentUser) => {
    if (!firebaseUser) {
      setError(
        t("invite.acceptError", {
          defaultValue: "Could not accept the invite.",
        })
      );
      return;
    }

    setError("");
    setInfo("");
    setIsAcceptingInvite(true);

    try {
      const acceptInvite = httpsCallable(functions, "accept_member_invite");
      await firebaseUser.getIdToken();
      await acceptInvite(payload);
      await firebaseUser.getIdToken(true);

      if (!firebaseUser.emailVerified) {
        await sendVerificationAndReturnToLogin(firebaseUser);
        return;
      }

      await authenticateUser({ forceRefresh: true });
      navigate("/welcome", { replace: true });
    } catch (acceptError) {
      setError(
        extractErrorMessage(
          acceptError,
          t("invite.acceptError", {
            defaultValue: "Could not accept the invite.",
          })
        )
      );
    } finally {
      setIsAcceptingInvite(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!invitePreview) return;

    if (!displayName.trim() || !password || !passwordConfirm) {
      setError(
        t("signup.missingFields", { defaultValue: "Please fill all required fields." })
      );
      return;
    }

    if (!isSecurePassword(password)) {
      setError(
        t("signup.passwordSecurity", {
          defaultValue:
            "Password must have at least 8 characters, uppercase, lowercase, number, and symbol.",
        })
      );
      return;
    }

    if (password !== passwordConfirm) {
      setError(
        t("signup.passwordMismatch", {
          defaultValue: "Passwords do not match.",
        })
      );
      return;
    }

    setError("");
    setInfo("");
    setIsCreatingAccount(true);

    try {
      const completeInviteSignup = httpsCallable(functions, "complete_member_invite_signup");
      await completeInviteSignup({
        ...payload,
        displayName: displayName.trim(),
        password,
      });

      const userCred = await signInAfterInviteSignup(invitePreview.email, password);
      await sendVerificationAndReturnToLogin(userCred.user);
    } catch (signupError) {
      const errorCode = String(signupError?.code || "");
      if (errorCode.includes("already-exists")) {
        setShowSignInForm(true);
        setInfo(
          t("invite.accountExists", {
            defaultValue: "This invite email already has an account. Sign in below to accept the invite.",
          })
        );
      } else {
        setError(
          extractErrorMessage(
            signupError,
            t("invite.createAccountError", {
              defaultValue: "Could not create the invited account.",
            })
          )
        );
      }
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleSignInAndAccept = async () => {
    if (!invitePreview?.email || !loginPassword) {
      setError(
        t("login.fillEmailAndPassword", {
          defaultValue: "Please enter your email and password.",
        })
      );
      return;
    }

    setError("");
    setInfo("");
    setIsSigningIn(true);

    try {
      const userCred = await signInWithEmailAndPassword(auth, invitePreview.email, loginPassword);
      await handleAcceptForSignedInUser(userCred.user);
    } catch (signInError) {
      setError(
        extractErrorMessage(
          signInError,
          t("login.invalidpassword", { defaultValue: "Invalid user or password *" })
        )
      );
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth).catch(() => {});
  };

  if (!isValidLink) {
    return (
      <div>
        <LanguageMenu />
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
          <Card style={{ maxWidth: 560, width: "100%" }}>
            <Typography.Title level={3}>
              {t("invite.invalidTitle", { defaultValue: "Invalid invite link" })}
            </Typography.Title>
            <Typography.Paragraph>
              {t("invite.invalidDescription", {
                defaultValue: "This invite URL is incomplete or invalid.",
              })}
            </Typography.Paragraph>
          </Card>
        </div>
        <Footer variant="plain" />
      </div>
    );
  }

  const roleLabel = invitePreview ? getFleetRoleLabel(invitePreview.role, t) : "";
  const title = invitePreview
    ? t("invite.title", {
        fleetName: invitePreview.fleetName || invitePreview.fleetId,
        defaultValue: `Join ${invitePreview.fleetName || invitePreview.fleetId}`,
      })
    : t("invite.loading", { defaultValue: "Loading invite..." });

  return (
    <div>
      <LanguageMenu />
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <Card style={{ maxWidth: 560, width: "100%" }} loading={previewLoading}>
          <Typography.Title level={3}>{title}</Typography.Title>
          {invitePreview ? (
            <Typography.Paragraph>
              {t("invite.subtitle", {
                role: roleLabel,
                defaultValue: `You were invited to join this fleet as ${roleLabel}.`,
              })}
            </Typography.Paragraph>
          ) : null}
          {error ? <Alert type="error" message={error} style={{ marginBottom: 16 }} /> : null}
          {info ? <Alert type="info" message={info} style={{ marginBottom: 16 }} /> : null}

          {invitePreview ? (
            <>
              <Form layout="vertical">
                <Form.Item
                  label={t("invite.emailLabel", { defaultValue: "Invited email" })}
                  style={{ marginBottom: 12 }}
                >
                  <Input value={invitePreview.email} readOnly />
                </Form.Item>
              </Form>

              {isLoading ? (
                <Typography.Text>
                  {t("common.loading", { defaultValue: "Loading..." })}
                </Typography.Text>
              ) : null}

              {!isLoading && currentUser && !isInviteEmailUser ? (
                <>
                  <Alert
                    type="warning"
                    style={{ marginBottom: 16 }}
                    message={t("invite.mismatchAccount", {
                      email: invitePreview.email,
                      currentEmail: currentUser.email || "",
                      defaultValue: `This invite is for ${invitePreview.email}, but you are signed in as ${
                        currentUser.email || ""
                      }.`,
                    })}
                  />
                  <Button onClick={handleSignOut}>
                    {t("invite.signOutButton", { defaultValue: "Sign out" })}
                  </Button>
                </>
              ) : null}

              {!isLoading && isInviteEmailUser ? (
                <>
                  <Typography.Paragraph>
                    {t("invite.alreadySignedIn", {
                      email: currentUser.email || invitePreview.email,
                      defaultValue: `Signed in as ${currentUser.email || invitePreview.email}.`,
                    })}
                  </Typography.Paragraph>
                  <Button
                    type="primary"
                    loading={isAcceptingInvite}
                    onClick={() => handleAcceptForSignedInUser(currentUser)}
                  >
                    {t("invite.acceptButton", { defaultValue: "Accept invite" })}
                  </Button>
                </>
              ) : null}

              {!isLoading && !currentUser ? (
                <>
                  {!showSignInForm ? (
                    <>
                      <Typography.Title level={4}>
                        {t("invite.createAccountTitle", { defaultValue: "Set your password" })}
                      </Typography.Title>
                      <Typography.Paragraph>
                        {t("invite.createAccountHelp", {
                          defaultValue: "Create your account and accept this invite in one step.",
                        })}
                      </Typography.Paragraph>
                      <Form layout="vertical" onFinish={handleCreateAccount}>
                        <Form.Item
                          label={t("invite.nameLabel", { defaultValue: "Name" })}
                        >
                          <Input
                            value={displayName}
                            onChange={(event) => setDisplayName(event.target.value)}
                            placeholder={t("invite.namePlaceholder", { defaultValue: "Full name" })}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t("signup.password", { defaultValue: "Password" })}
                          validateStatus={password.length > 0 && !isSecurePassword(password) ? "error" : ""}
                          help={
                            password.length > 0 && !isSecurePassword(password)
                              ? t("signup.passwordSecurity", {
                                  defaultValue:
                                    "Password must have at least 8 characters, uppercase, lowercase, number, and symbol.",
                                })
                              : null
                          }
                        >
                          <Input.Password
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            placeholder={t("signup.password", { defaultValue: "Password" })}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t("signup.confirmpassword", { defaultValue: "Confirm Password" })}
                          validateStatus={
                            passwordConfirm.length > 0 && passwordConfirm !== password ? "error" : ""
                          }
                          help={
                            passwordConfirm.length > 0 && passwordConfirm !== password
                              ? t("signup.passwordMismatch", {
                                  defaultValue: "Passwords do not match.",
                                })
                              : null
                          }
                        >
                          <Input.Password
                            value={passwordConfirm}
                            onChange={(event) => setPasswordConfirm(event.target.value)}
                            placeholder={t("signup.confirmpassword", { defaultValue: "Confirm Password" })}
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isCreatingAccount}
                          disabled={!canCreateAccount}
                          block
                        >
                          {t("invite.createAccountButton", { defaultValue: "Create account" })}
                        </Button>
                      </Form>
                      <Divider />
                      <Button type="link" onClick={() => setShowSignInForm(true)} style={{ padding: 0 }}>
                        {t("invite.existingAccountButton", {
                          defaultValue: "I already have an account",
                        })}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography.Title level={4}>
                        {t("invite.signInTitle", { defaultValue: "Already have an account?" })}
                      </Typography.Title>
                      <Typography.Paragraph>
                        {t("invite.signInHelp", {
                          defaultValue: "Sign in with the invited email to accept the invite.",
                        })}
                      </Typography.Paragraph>
                      <Form layout="vertical" onFinish={handleSignInAndAccept}>
                        <Form.Item label={t("login.password", { defaultValue: "Password" })}>
                          <Input.Password
                            value={loginPassword}
                            onChange={(event) => setLoginPassword(event.target.value)}
                            placeholder={t("login.password", { defaultValue: "Password" })}
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isSigningIn || isAcceptingInvite}
                          disabled={!loginPassword}
                          block
                        >
                          {t("invite.signInButton", { defaultValue: "Sign in and accept invite" })}
                        </Button>
                      </Form>
                      <Divider />
                      <Button type="link" onClick={() => setShowSignInForm(false)} style={{ padding: 0 }}>
                        {t("invite.createInsteadButton", {
                          defaultValue: "Create a new account instead",
                        })}
                      </Button>
                    </>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <Typography.Paragraph>
              {t("invite.loadError", { defaultValue: "Could not load this invite." })}
            </Typography.Paragraph>
          )}
        </Card>
      </div>
      <Footer variant="plain" />
    </div>
  );
}
